// The diameter a wheel is sized at before any pinch (2026-10-05).
//
// Riley: "Default, use the GLB's size. If size(s) in the payload, use
// that instead." Many wheels send no size; the GLB is authored in meters,
// so its face diameter is the default and a payload size outranks it.
//
// The ONE base the header pill, the adjustment panel and the pinch snap
// all read — they each derived it on their own before, with three
// different fallbacks.

import type { WheelSize } from '@/models/wheel-size'
import { MAX_SNAPPED_DIAMETER_IN, MIN_SNAPPED_DIAMETER_IN } from '@/models/wheel-diameter-snap'

const METERS_PER_INCH = 0.0254

/** The GLB measures edge to edge — over the lip — so the wheel's size is
 *  its diameter less this (Riley, 2026-10-05: 22.3" edge to edge is a 21"
 *  wheel). */
export const GLB_LIP_ALLOWANCE_IN = 1.5

/**
 * A GLB face diameter (meters, as authored) to a wheel size: less the lip
 * allowance, to the nearest whole inch.
 *
 * Null when it is not plausibly a wheel — a GLB authored in centimeters
 * (the case the renderer's normalisation exists for) measures 100x out,
 * and a ladder centred on 2000" is worse than none. A GLB that carries a
 * tire measures the tire: a guesstimate, which the +/-3" ladder absorbs.
 */
export function glbDiameterInches(faceDiameterM: number | null | undefined): number | null {
  if (faceDiameterM == null || !Number.isFinite(faceDiameterM) || faceDiameterM <= 0) return null
  const inches = Math.round(faceDiameterM / METERS_PER_INCH - GLB_LIP_ALLOWANCE_IN)
  return inches >= MIN_SNAPPED_DIAMETER_IN && inches <= MAX_SNAPPED_DIAMETER_IN ? inches : null
}

type TBaseDiameterInput = {
  wheelInfo: { selectedSize: WheelSize | null; assembledSize: WheelSize | null }
  glbDiameterInches: number | null | undefined
}

const usable = (d: number | null | undefined): d is number =>
  d != null && Number.isFinite(d) && d > 0

/**
 * The wheel's base diameter and where it came from: the payload's selected
 * size, else its assembled size ('payload'), else the GLB's own size — an
 * ESTIMATE ('glb'). Null when none is usable.
 */
export function resolveBaseDiameter({
  wheelInfo,
  glbDiameterInches: glb,
}: TBaseDiameterInput): { inches: number; source: 'payload' | 'glb' } | null {
  for (const d of [wheelInfo.selectedSize?.diameterInches, wheelInfo.assembledSize?.diameterInches]) {
    if (usable(d)) return { inches: d, source: 'payload' }
  }
  return usable(glb) ? { inches: glb, source: 'glb' } : null
}

/** resolveBaseDiameter, the inches only. */
export function resolveBaseDiameterInches(input: TBaseDiameterInput): number | null {
  return resolveBaseDiameter(input)?.inches ?? null
}
